import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { dashboardService } from "@/services/dashboardService";
import { userPreferencesService } from "@/services/userPreferencesService";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { StatusBadge, PriorityBadge } from "@/utils/constants";
import { DateRangePicker } from "@/components/DateRangePicker";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { Ticket, AlertTriangle, CheckCircle, Clock, ArrowRight, Settings2, CircleCheck } from "lucide-react";
import { toast } from "sonner";

const CHART_COLORS = ["#3B82F6", "#8B5CF6", "#003DA5", "#F59E0B", "#10B981", "#52525B", "#EF4444"];
const AXIS_TICK = { fontSize: 11 };
const BAR_RADIUS = [2, 2, 0, 0];
const WIDGET_OPTIONS = [["metrics", "Metric Cards"], ["status_chart", "Status Chart"], ["priority_chart", "Priority Chart"], ["recent_tickets", "Recent Tickets"]];

function toISODate(date) { return date ? new Date(date.getFullYear(), date.getMonth(), date.getDate()).toISOString() : ""; }
function toISODateEnd(date) { return date ? new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59).toISOString() : ""; }

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(undefined);
  const [visibleWidgets, setVisibleWidgets] = useState(["metrics", "status_chart", "priority_chart", "recent_tickets"]);

  const fetchStats = useCallback(() => {
    setLoading(true);
    const params = {};
    if (dateRange?.from) params.date_from = toISODate(dateRange.from);
    if (dateRange?.to) params.date_to = toISODateEnd(dateRange.to);
    dashboardService.getStats(params).then(({ data }) => setStats(data)).catch(() => {}).finally(() => setLoading(false));
  }, [dateRange]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => {
    userPreferencesService.getDashboard().then(({ data }) => {
      if (data.visible_widgets) setVisibleWidgets(data.visible_widgets);
    }).catch(() => {});
  }, []);

  const toggleWidget = async (widget) => {
    const next = visibleWidgets.includes(widget) ? visibleWidgets.filter((w) => w !== widget) : [...visibleWidgets, widget];
    setVisibleWidgets(next);
    try { await userPreferencesService.updateDashboard({ visible_widgets: next, widget_order: next }); } catch { toast.error("Failed to save preferences"); }
  };

  const show = (w) => visibleWidgets.includes(w);

  if (loading) return <DashboardSkeleton />;
  if (!stats) return null;

  return (
    <div className="p-6 space-y-6" data-testid="dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-[#09090B]" data-testid="dashboard-title">Dashboard</h1>
          <p className="text-sm text-[#52525B] mt-0.5">Welcome back, {user?.name}</p>
        </div>
        <div className="flex items-center gap-2">
          <DateRangePicker dateRange={dateRange} onDateRangeChange={setDateRange} />
          <CustomizePopover visibleWidgets={visibleWidgets} toggleWidget={toggleWidget} />
        </div>
      </div>

      {show("metrics") && <MetricsGrid stats={stats} userRole={user?.role} />}

      {user?.role !== "end_user" && (show("status_chart") || show("priority_chart")) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {show("status_chart") && <StatusChart stats={stats} />}
          {show("priority_chart") && <PriorityChart stats={stats} />}
        </div>
      )}

      {show("recent_tickets") && <RecentTickets tickets={stats.recent_tickets} navigate={navigate} />}
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="p-6" data-testid="dashboard-loading">
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-zinc-200 rounded-sm" />
        <div className="grid grid-cols-5 gap-4">{[1, 2, 3, 4, 5].map((i) => <div key={i} className="h-24 bg-zinc-200 rounded-sm" />)}</div>
      </div>
    </div>
  );
}

function CustomizePopover({ visibleWidgets, toggleWidget }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 rounded-sm gap-1 text-xs" data-testid="dashboard-customize-btn"><Settings2 size={14} /> Customize</Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-3" align="end">
        <p className="text-xs uppercase tracking-[0.15em] font-bold text-[#52525B] mb-2">Visible Widgets</p>
        <div className="space-y-2">
          {WIDGET_OPTIONS.map(([key, label]) => (
            <div key={key} className="flex items-center gap-2">
              <Checkbox id={`widget-${key}`} checked={visibleWidgets.includes(key)} onCheckedChange={() => toggleWidget(key)} data-testid={`toggle-widget-${key}`} />
              <Label htmlFor={`widget-${key}`} className="text-sm text-[#52525B]">{label}</Label>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function MetricsGrid({ stats, userRole }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
      <MetricCard testId="metric-total" icon={<Ticket size={18} />} label="Total Tickets" value={stats.total_tickets} color="text-[#003DA5]" bgColor="bg-[#003DA5]/5" />
      <MetricCard testId="metric-open" icon={<Clock size={18} />} label="Open" value={stats.open_tickets} color="text-blue-600" bgColor="bg-blue-50" />
      <MetricCard testId="metric-in-progress" icon={<ArrowRight size={18} />} label="In Progress" value={stats.in_progress_tickets} color="text-sky-600" bgColor="bg-sky-50" />
      <MetricCard testId="metric-closed" icon={<CircleCheck size={18} />} label="Closed" value={stats.closed_tickets || 0} color="text-emerald-600" bgColor="bg-emerald-50" />
      <MetricCard testId="metric-overdue" icon={<AlertTriangle size={18} />} label="Overdue" value={stats.overdue_tickets} color="text-red-600" bgColor="bg-red-50" />
    </div>
  );
}

function StatusChart({ stats }) {
  const data = Object.entries(stats.status_breakdown || {}).map(([key, val]) => ({ name: key.replace("_", " "), value: val }));
  return (
    <Card className="border-[#E4E4E7] shadow-none rounded-sm">
      <CardHeader className="pb-2"><CardTitle className="font-heading text-sm font-bold tracking-tight">Tickets by Status</CardTitle></CardHeader>
      <CardContent>
        <div className="h-[220px]" data-testid="status-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data}><CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" /><XAxis dataKey="name" tick={AXIS_TICK} /><YAxis tick={AXIS_TICK} allowDecimals={false} /><Tooltip /><Bar dataKey="value" fill="#003DA5" radius={BAR_RADIUS} /></BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

function PriorityChart({ stats }) {
  const data = Object.entries(stats.priority_breakdown || {}).map(([key, val]) => ({ name: key, value: val }));
  return (
    <Card className="border-[#E4E4E7] shadow-none rounded-sm">
      <CardHeader className="pb-2"><CardTitle className="font-heading text-sm font-bold tracking-tight">Tickets by Priority</CardTitle></CardHeader>
      <CardContent>
        <div className="h-[220px]" data-testid="priority-chart">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart><Pie data={data} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>{data.map((entry) => <Cell key={entry.name} fill={CHART_COLORS[data.indexOf(entry) % CHART_COLORS.length]} />)}</Pie><Tooltip /></PieChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

function RecentTickets({ tickets, navigate }) {
  return (
    <Card className="border-[#E4E4E7] shadow-none rounded-sm">
      <CardHeader className="pb-2"><CardTitle className="font-heading text-sm font-bold tracking-tight">Recent Tickets</CardTitle></CardHeader>
      <CardContent>
        {!tickets?.length ? (
          <div className="py-8 text-center" data-testid="no-recent-tickets">
            <Ticket size={28} className="mx-auto text-[#D4D4D8] mb-2" />
            <p className="text-sm text-[#71717A]">No tickets yet</p>
          </div>
        ) : (
          <div className="space-y-0 divide-y divide-[#F4F4F5]" data-testid="recent-tickets-list">
            {tickets.map((ticket) => (
              <button key={ticket.id} onClick={() => navigate(`/tickets/${ticket.id}`)} className="w-full flex items-center justify-between py-3 px-2 hover:bg-[#F8FAFC] text-left rounded-sm" data-testid={`recent-ticket-${ticket.ticket_number}`}>
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <span className="text-xs font-mono text-[#71717A] shrink-0">{ticket.ticket_number}</span>
                  <span className="text-sm text-[#09090B] truncate hover:text-[#003DA5]">{ticket.title}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-4">
                  <PriorityBadge priority={ticket.priority} />
                  <StatusBadge status={ticket.status} />
                </div>
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MetricCard({ testId, icon, label, value, color, bgColor }) {
  return (
    <Card className="border-[#E4E4E7] shadow-none rounded-sm" data-testid={testId}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`p-2 rounded-sm ${bgColor}`}><span className={color}>{icon}</span></div>
        <div>
          <p className="text-2xl font-bold font-heading text-[#09090B]">{value}</p>
          <p className="text-xs text-[#52525B] uppercase tracking-[0.1em] font-bold mt-0.5">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}
